import React, { useMemo } from 'react'
import { useFormikContext } from 'formik'
import tw from 'twin.macro'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { UploadedFile } from 'src/components/shared/UploadedFile'
import { FileUploadInput } from 'src/components/shared/FileUploadInput'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { Button } from 'src/components/shared/Button'
import { Market } from 'src/interfaces/market'
import { getOnBoardingStepsNames } from '../InviteWorkersUtils'

interface InviteWorkersViaCSVFormContentProps {
  isLoading: boolean
  file: File | null
  markets: Market[]
  onboardingTemplates: any[]
  onRemoveFile: () => void
  onSelectFile: (file: File) => void
}

interface InviteWorkersViaCSVFormValues {
  collectDataTemplateId: string
  partnerMarketId: string
}

const ACCESS_TYPES = ['text/csv']

export const InviteWorkersViaCSVFormContent: React.FC<InviteWorkersViaCSVFormContentProps> = ({
  isLoading,
  file,
  markets,
  onboardingTemplates,
  onRemoveFile,
  onSelectFile,
}) => {
  const { values, handleSubmit } = useFormikContext<InviteWorkersViaCSVFormValues>()

  const stepNames = useMemo(() => {
    return getOnBoardingStepsNames(onboardingTemplates, values.collectDataTemplateId)
  }, [values.collectDataTemplateId, onboardingTemplates])

  const showOnboardingSteps = Boolean(values.collectDataTemplateId && stepNames.trim())

  const selectedTemplate = useMemo(
    () => onboardingTemplates.find((t) => t.id === values.collectDataTemplateId),
    [values.collectDataTemplateId, onboardingTemplates],
  )

  return (
    <div data-testid="contractors-onboarding-invite-csv-form">
      <div css={tw`px-4 pt-5`}>
        <div css={tw`flex gap-5 mb-4`}>
          <div css={tw`flex-1`}>
            <FormikSelect
              css={tw`w-full`}
              name="collectDataTemplateId"
              label="Onboarding flow"
              placeholder="Select flow"
              options={onboardingTemplates.map((item) => ({
                label: item.name,
                value: item.id,
              }))}
              inputProps={{
                'data-testid': 'contractors-onboarding-invite-csv-flow-select-input',
              }}
            />
          </div>

          <div css={tw`flex-1`}>
            <FormikSelect
              css={tw`w-full`}
              name="partnerMarketId"
              label="Market"
              placeholder="Select market"
              options={markets.map((item) => ({
                label: item.title,
                value: item.id,
              }))}
              inputProps={{
                'data-testid': 'contractors-onboarding-invite-csv-market-select-input',
              }}
            />
          </div>
        </div>
        {showOnboardingSteps && (
          <div css={tw`flex mt-3 mb-5 p-0`}>
            <div css={tw`w-1 bg-[#8A2BE2] rounded mr-0`} />
            <div css={tw`pl-3 flex-1`}>
              <Typography css={tw`text-sm font-semibold mb-1`}>
                {selectedTemplate?.name || 'Selected Flow'} Step(s)
              </Typography>
              <Typography css={tw`text-sm text-[#847E96]`}>{stepNames}</Typography>
            </div>
          </div>
        )}
        <div>
          <Typography
            variant="overline"
            color={colors.GRAY_COOL_GRAY}
            css={tw`normal-case text-[14px]`}
          >
            Upload file
          </Typography>
          <div css={tw`bg-white rounded-xl mt-2`}>
            {file ? (
              <UploadedFile file={file} onRemove={onRemoveFile} />
            ) : (
              <FileUploadInput
                fileSizeLimit={10000000} // 10 MB
                acceptTypes={ACCESS_TYPES}
                inputDataTestId="contractors-onboarding-invite-csv-file-input"
                onSelect={onSelectFile}
              />
            )}
          </div>
        </div>
      </div>

      <div
        css={tw`flex flex-row justify-end items-end border-0 border-t border-solid border-[#EDEDED] mt-4 p-4 pt-3`}
      >
        <div css={tw`flex flex-col items-start`}>
          <Button
            variant="contained"
            size="small"
            loading={isLoading}
            disabled={!file}
            onClick={() => handleSubmit()}
            data-testid="contractors-onboarding-invite-csv-submit-btn"
          >
            Invite contractors
          </Button>
        </div>
      </div>
    </div>
  )
}
